import React, { useEffect, useRef } from 'react';
import './Projects.css';

const Projects = () => {
  const projectsRef = useRef(null);
  
  useEffect(() => {
    const node = projectsRef.current;

    if (!node) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if(!entry) return;

        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
      observer.disconnect();
    }
  }, [])

  const projects = [
    {
      title: "F1 Data Engineering Project",
      status: "In Progress",
      description: "Building an end-to-end data engineering pipeline for Formula 1 analytics using the Jolpica API, implementing automated ingestion,PostgreSQL staging design, and scalable loading strategies to handle real-world API constraints.",
      features: [
        "Developing API-driven ingestion pipelines with pagination and rate-limit handling",
        "Designing PostgreSQL staging tables to safely load and validate raw race data",
        "Implementing duplicate-safe and NULL-safe data loading using bulk inserts and conflict handling",
        "Automating ingestion across seasons and race rounds for scalable historical and incremental loads"
      ],
      techStack: ["Python", "PostgreSQL", "REST APIs (Jolpica)", "pandas", "psycopg2", "SQL"],
      github: "https://github.com/BMIHALKO/F1-Data-Platform",
      website: "",
      tags: ["Data Engineering", "ETL", "API", "Analytics"]
    },
    {
      title: "StackExchange Behavior Analytics Platform - Revature",
      status: "Completed",
      description: "Built a data pipeline that processes StackExchange question activity using both streaming and batch processing",
      features: [
        "Designed a Kafka-based ingestion pipeline using the StackExchange API",
        "Processed streaming data with Spark Structured Streaming and stored paritioned Parquet datasets",
        "Implemented batch ELT using Spark RDD and DataFrame APIs for analytics outpu generation",
        "Orchestrated the full pipeline using Airflow DAGs with validation and transformation steps"
      ],
      techStack: ["Python", "PySpark", "Kafka", "Airflow", "Parquet", "Docker"],
      github: "https://github.com/BMIHALKO/StackExchange-Behavior-Analytics-Platform",
      website: "",
      tags: ["Data Engineering", "Streaming", "ETL", "Analytics"]
    },
        {
      title: "Capstone Project - Firebase Firestore Repository",
      status: "Completed",
      description: "Designed and developed a full-stack data repository using Firebase Firestore. Led a Scrum team as Scrum Master, integrating backend and frontend components to build a scalable data management system for a real client.",
      features: [
        "Scrum team leadership and sprint planning",
        "Backend integration with Firebase Firestore",
        "Real-time data synchronization",
        "User authentication and security",
      ],
      techStack: ["React", "Firebase", "Firestore", "JavaScript", "Dart", "Flutter", "Node.js", "HTML", "CSS"],
      tags: ["React", "Firebase", "Firestore", "Full-Stack"]
    },
        {
      title: "AI Job Application Command Center",
      status: "In Progress",
      description: "A personal job application tracking dashboard focused on speed and clarity-centralizing application details, statuses, priorities, notes, and next actions in a clean UI designed for daily use.",
      features: [
        "Application list with search + filters (status, priority, work mode)",
        "Dynamic application detail pages with deep linking and empty/invalid ID fallback UI",
        "Status + priority visualization with consistent badge mapping",
        "Notes rendering upgrade (preserve line breaks, graceful empty states)",
        "Designed for persistence (migrating from mock data to Firebase/Firestore)",
      ],
      techStack: ["Next.js", "React", "TypeScript", "Firebase", "Firestore", "CSS"],
      github: "https://github.com/BMIHALKO/Ai-Job-Application-Command-Center",
      website: "",
      tags: ["Full-Stack", "Dashboard", "Next.js", "Firebase", "UI/UX"]
    },
    {
      title: "Transformer-Based Neural Machine Translation (EN → DE)",
      status: "Completed",
      description: "Implemented and trained a Transformer model for English → German neural machine translation, including data preprocessing, training/evaluation, and qualitative translation examples with tracked metrics.",
      features: [
        "Built Transformer architecture (attention, embeddings, positional encoding) and full training pipeline",
        "Evaluated model performance with saved metrics and sample translations for qualitative review",
        "Reproducible project structure with clear documentation and experiment outputs"
      ],
      techStack: ["Python", "PyTorch", "NLP", "Transformers", "Attention", "Tokenization"],
      github: "https://github.com/BMIHALKO/tiny-transformer-final",
      website: "",
      tags: ["NLP", "Machine Translation", "Transformers", "PyTorch", "Deep Learning"]
    },
    {
      title: "Social Media Blog Post - Revature",
      status: "Completed",
      description: "A backend REST API for a social media application built without a front end. Implemented functionality for user account management and message operations including creation, updates, deletion, and retrievals. Developed endpoints for user login, registration, and viewing public or user-specific posts.",
      features: [
        "User login and registration endpoints",
        "Post creation, editing, and deletion",
        "Retrieve all messages or messages by user",
        "Data access with JDBC and Java"
      ],
      techStack: ["Java", "Javalin", "JDBC"],
      github: "https://github.com/BMIHALKO/BMIHALKO-pep-project.git",
      website: "",
      tags: ["Java", "Backend", "REST API"]
    },
    {
      title: "Spring Social Media Blog Post API - Revature",
      status: "Completed",
      description: "A RESTful backend application build using the Spring Framework. Similar to the previous project, this app manages user accounts and blog messages via API endpoints. Leveraged Spring Boot and Spring Data for clean architecture and maintainability.",
      features: [
        "Spring Boot project setup",
        "CRUD operations on user messages",
        "Spring-based login and registration flows",
        "Database persistence with Spring Data"
      ],
      techStack: ["Java", "Spring Boot", "Spring Data", "Spring Web"],
      github: "https://github.com/BMIHALKO/BMIHALKO-pep-spring-project.git",
      website: "",
      tags: ["Java", "Spring Boot", "REST", "API"]
    },
  ];

  return (
    <section className="projects-section" id="projects">
      <div ref={projectsRef} className="projects-container">
        <h2 className="project-heading">My <span className="highlight">Projects</span></h2>
        
        <div className="projects-grid">
          {projects.map((project, index) => (
            <div className="project-card" key={index}>
              <div className="project-header">
                <h3 className="project-title">{project.title}</h3>
                <div className="project-meta">
                  <span className={`project-status ${project.status.toLowerCase().replace(' ', '-')}`}>
                    {project.status}
                  </span>
                </div>
              </div>
              
              <p className="project-description">{project.description}</p>
              
              <div className="project-features">
                <ul>
                  {project.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </div>
              
              <div className="tech-stack">
                {project.techStack.map((tech, i) => (
                  <span className="tech-badge" key={i}>{tech}</span>
                ))}
              </div>
              
              <div className="project-tags">
                {project.tags.map((tag, i) => (
                  <span className="project-tag" key={i}>{tag}</span>
                ))}
              </div>
              
              <div className="project-links">
                {project.github && (
                  <a href={project.github} target="_blank" rel="noopener noreferrer" className="github-link">
                    <i className="fab fa-github"></i> GitHub
                  </a>
                )}
                {project.website && (
                  <a href={project.website} target="_blank" rel="noopener noreferrer" className="website-link">
                    <i className="fas fa-globe"></i> Website
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;